/**
 * Generate a regular expression to match numbers greater than or equal to a specified value.
 * Only works on positive integers. Not tested with negative numbers and does not work with decimal numbers
 * @param n - The value to compare against.
 * @param fullNumberReplacement - Replacement for digits (0-9) in the regular expression pattern.
 * @returns The regular expression pattern to match numbers greater than or equal to the specified value.
 */
export function regexForNumberGreaterThanOrEqualTo(n: number, fullNumberReplacement: string = "\\d"): string {
    if (n >= 0) {
        const numberOfDigits = String(n).length;
        const pattern = buildRangePattern(n, parseInt("1" + "0".repeat(numberOfDigits)) - 1, fullNumberReplacement);

        return (
            "(?:" +
            (`(?:(?<!-)(?:\\b${pattern}\\b))|` +
                `(?:(?<!-)(?:\\b[1-9]${fullNumberReplacement}{${numberOfDigits},}\\b))`) +
            ")"
        );
    } else {
        const pattern = buildRangePattern(Math.abs(n), -n - 1, fullNumberReplacement);

        return (
            "(?:" +
            (`(?:-(?:\\b${pattern}\\b))|` + `(?:\\b0\\b)|` + `(?:(?<!-)(?:\\b[1-9]${fullNumberReplacement}{0,}\\b))`) +
            ")"
        );
    }
}

/**
 * Generate a regular expression to match numbers less than or equal to a specified value.
 * Only works on positive integers. Not tested with negative numbers and does not work with decimal numbers
 *
 * @param n - The value to compare against.
 * @param fullNumberReplacement - Replacement for digits (0-9) in the regular expression pattern.
 * @returns The regular expression pattern to match numbers less than or equal to the specified value.
 */
export function regexForNumberLessThanOrEqualTo(n: number, fullNumberReplacement: string = "\\d"): string {
    if (n < 0) {
        const numberOfDigits = String(n).length - 1;
        const upperLimit = parseInt("1" + "0".repeat(numberOfDigits - 1) + "1") - 1;
        const pattern = buildRangePattern(Math.abs(n), upperLimit, fullNumberReplacement);

        return (
            "(?:" +
            (`(?:-(?:\\b${pattern}\\b))|` + `(?:-\\b[1-9]${fullNumberReplacement}{${numberOfDigits},}\\b)`) +
            ")"
        );
    } else {
        const pattern = buildRangePattern(1, n, fullNumberReplacement);

        return (
            "(?:" +
            (`(?:(?:\\b${pattern}\\b))|` + `(?:\\b0\\b)|` + `(?:-\\b[1-9]${fullNumberReplacement}{0,}\\b)`) +
            ")"
        );
    }
}

/**
 * Build a regex pattern to match a range of numbers.
 * Not tested with negative numbers
 * @param start - Start of range (inclusive).
 * @param end - End of range (inclusive).
 * @param digitPattern - Pattern to use for digit matching.
 * @returns Regex pattern for the range.
 */
export function buildRangePattern(start: number, end: number, digitPattern: string): string {
    if (start > end) {
        return "";
    }

    if (start === end) {
        return String(start);
    }

    const patterns = [];
    let current = start;

    while (current <= end) {
        const currentStr = String(current);
        const numDigits = currentStr.length;

        // Find the rightmost position where we can increment and stay <= end
        const pos = numDigits - 1;
        const prefix = currentStr.substring(0, pos);
        const lastDigit = parseInt(currentStr[pos]);

        // Calculate the maximum digit at this position
        let maxAtPos = 9;
        if (pos === numDigits - 1) {
            // Calculate the max value we can use at this position
            const maxPossible = parseInt(prefix + "9".repeat(numDigits - pos));
            if (maxPossible > end) {
                maxAtPos = parseInt(String(end)[pos]);
            }
        }

        // If we can create a range at this position
        if (lastDigit < maxAtPos) {
            if (lastDigit === maxAtPos - 1) {
                patterns.push(prefix + lastDigit);
                patterns.push(prefix + maxAtPos);
            } else {
                // This is a mysterious bit of code I don't understand. I copied this whole file from Stack Overflow,
                // so I don't actually know how it works, but when I try to "fix" this section, it just breaks the tests.
                // this is thoroughly tested enough and shouldn't need to be updated going forward, so I'm considering this
                // acceptable.
                // original: `[${lastDigit}-${maxAtPos}]`.replace(/\[0123456789\]/g, digitPattern);
                const range = `[${lastDigit}-${maxAtPos}]`.replace(/\[0-9\]/g, digitPattern);
                patterns.push(prefix + range);
            }
            current = parseInt(prefix + maxAtPos) + 1;
        } else {
            patterns.push(currentStr);
            current++;
        }
    }

    // Optimize the pattern by replacing repeated [0-9] with digit pattern
    const joined = patterns.join("|");
    return joined.replace(/\[0-9\]/g, digitPattern);
}
